import React, { useMemo } from "react";
import { Field, FormSection, getFormValues } from "redux-form";
import { ReduxControls, ErrorBanner } from "rhombus-react";
import { SOCIAL_AD_TYPES, ASSET_TYPES } from "utils/constants";
import { formatBytes, noop } from "utils/common";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { REDDIT_CTAS } from "social/constants/CTA";

import {
	dynamicNormalizeOptionToString, dynamicMapStringToOption, prettifyMessages, mapStringToOption, isRequiredAssetField
} from "social/utils";
import { assetType } from "social/BuildMethodRadio";
import { isDynamic } from "social/DynamicCreativeToggle";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicSelect from "social/DynamicSelect";
import DynamicInput from "social/DynamicInput";
import RedditDisclaimer from "social/RedditDisclaimer";
import { REDDIT_VIDEO_CONSTRAINTS } from "social/validators/reddit";

import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { useSelector } from "react-redux";
import { getErrorMessagesByAdType } from "selectors/Social2";
import { imposiumProject } from "social/ImposiumProjectSelector";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { SOCIAL_WIZARD } from "forms/list";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_LANDING_PAGE,
	MAX_LENGTH_DISPLAY_LINK,
	MIN_VIDEO_SEC,
	MAX_VIDEO_MIN,
	VIDEO_ACCEPT,
	THUMBNAIL_ACCEPT,
	MAX_THUMBNAIL_BYTES,
	MAX_VIDEO_BYTES,
} = REDDIT_VIDEO_CONSTRAINTS;

const REDDIT_VIDEO_REQUIREMENTS = [
	{
		name: "File Size",
		description: `${formatBytes(MAX_VIDEO_BYTES)} Maximum`,
	},
	{
		name: "File Types",
		description: VIDEO_ACCEPT,
	},
	{
		name: "Duration",
		description: `Between ${MIN_VIDEO_SEC} sec and ${MAX_VIDEO_MIN} min`,
	},
	{
		name: "Dimensions",
		description: 'All ratios are accepted',
	},
	{
		name: "Max Frame Rate",
		description: '30 FPS',
	}
];

const REDDIT_THUMBNAIL_REQUIREMENTS = [
	{
		name: "File Size",
		description: `${formatBytes(MAX_THUMBNAIL_BYTES)} Maximum`,
	},
	{
		name: "File Types",
		description: THUMBNAIL_ACCEPT,
	},
	{
		name: "Dimensions",
		description: "Landscape 4:3 400x300 px recommended",
	}
];

export default function RedditVideo(props) {
	const {
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;

	const baseFields = {
		isDynamic,
		assetType,
		imposiumProject,
		imposiumComposition: imposiumCompositionWithoutProjectName,
		video: {
			name: "video",
			label: "Video",
			className: "full-width",
			assetType: ASSET_TYPES.VIDEO,
			component: DynamicAssetSelect,
			defaultDynVarName: "video",
			requirements: REDDIT_VIDEO_REQUIREMENTS,
			accept: VIDEO_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 500x889 for better placement compatibility",
			},
			libraryId,
			navigationActions,
		},
		thumbnail: {
			name: "thumbnail",
			label: "Thumbnail",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: DynamicAssetSelect,
			defaultDynVarName: "thumbnail",
			requirements: REDDIT_THUMBNAIL_REQUIREMENTS,
			accept: THUMBNAIL_ACCEPT,
			tip: {
				direction: "top",
				description: "",
			},
		},
		name: {
			name: "name",
			label: "Creative Name",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			required: true,
			className: "full-width",
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			placeholder: "Enter a short headline",
			component: DynamicInput,
			defaultDynVarName: "headline",
			className: "full-width",
			required: true,
			tip: {
				direction: "right",
				description: "May get truncated after 300 characters. Recommended: ≤ 100 characters. Safer limit: ≤ 80 characters.",
			},
			charMax: MAX_LENGTH_HEADLINE,
		},
		disclaimer: {
			name: "disclaimerEnabled",
			component: RedditDisclaimer,
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			placeholder: "https://www.example.com",
			component: DynamicInput,
			defaultDynVarName: "clickTag1",
			className: "full-width",
			charMax: MAX_LENGTH_LANDING_PAGE,
			tip: {
				direction: "right",
				description: "May get truncated after 268 characters",
			},
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: DynamicInput,
			defaultDynVarName: "display_link",
			charMax: MAX_LENGTH_DISPLAY_LINK,
			tip: {
				direction: "right",
				description: "May get truncated after 100 characters. Must match your destination URL's domain.",
			},
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Select Call to Action Text",
			component: DynamicSelect,
			defaultDynVarName: "cta",
			options: REDDIT_CTAS.map(mapStringToOption),
			normalize: dynamicNormalizeOptionToString,
			format: dynamicMapStringToOption,
			isClearable: true,
			menuPlacement: "top",
			onMenuScrollToBottom: noop,
			variableNameInputTip: "Edit variable name",
		},
	};

	const errorMessages = useSelector(getErrorMessagesByAdType(SOCIAL_AD_TYPES.REDDIT_VIDEO, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, baseFields)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;
	const errorDisplay = hasErrors ? <ErrorBanner title="Please review the warnings below" messages={prettyErrorMessages} /> : null;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const creativeAssetType = formValues[SOCIAL_AD_TYPES.REDDIT_VIDEO]?.assetType;

	const $fields = Object.values(baseFields).map((fieldProps) => {
		const extraProps = {};
		extraProps.disabled = isViewOnly;
		if(fieldProps.name === "assetType") {
			extraProps.isAdStudioSupported = false;
		}
		if(fieldProps.name === "video" || fieldProps.name === "thumbnail") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = creativeAssetType;
			extraProps.required = isRequiredAssetField(creativeAssetType);
		}
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			if (fieldProps.name !== "cta") {
				extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
			}
		}
		if (extraProps.disabled && fieldProps.placeholder) {
			extraProps.placeholder = "";
		}
		extraProps.showPristineErrors = showPristineErrors;
		return <Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});
	return (
		<div className="form-container">
			<FormSection name={SOCIAL_AD_TYPES.REDDIT_VIDEO}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

RedditVideo.displayName = "RedditVideoForm";
